/**
 * SEO Schema Utilities for Déjà Vu Nomad
 * Generates JSON-LD structured data for better search engine visibility
 */

// Organization Schema - Use on homepage
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Déjà Vu Nomad",
  "alternateName": "DejaVu Nomad",
  "url": "https://dejavunomad.com",
  "logo": "https://dejavunomad.com/noBgColor.png",
  "sameAs": [
    "https://twitter.com/DejaVuNomadApp",
    "https://www.facebook.com/dejavunomad",
    "https://www.instagram.com/dejavunomad"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["English"]
  },
  "description": "Professional travel planning platform for group trips. Collaborative trip planning made easy with real-time chat, smart itineraries, and travel packages."
});

// WebSite Schema - For sitelinks search box
export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Déjà Vu Nomad",
  "url": "https://dejavunomad.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://dejavunomad.com/deals?search={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
});

// Travel Package Schema (Product + TouristTrip)
export const getPackageSchema = (pkg) => {
  if (!pkg) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": ["Product", "TouristTrip"],
    "name": pkg.title || pkg.name,
    "description": pkg.description || pkg.highlights?.join('. '),
    "image": pkg.cover_image || pkg.gallery?.[0],
    "url": `https://dejavunomad.com/packages/${pkg.slug}`,
    "provider": {
      "@type": "TravelAgency",
      "name": pkg.partner_name || pkg.company_name || "Déjà Vu Nomad",
      "url": pkg.partner_website || "https://dejavunomad.com"
    },
    "touristType": "Adventure traveler",
    "itinerary": {
      "@type": "ItemList",
      "numberOfItems": pkg.duration_days || 1,
      "itemListElement": pkg.items?.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "TouristAttraction",
          "name": item.name || item.title,
          "description": item.description
        }
      })) || []
    }
  };

  // Add destination
  if (pkg.destination) {
    schema.touristDestination = {
      "@type": "Place",
      "name": pkg.destination,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": pkg.destination
      }
    };
  }

  // Add pricing
  if (pkg.base_price) {
    schema.offers = {
      "@type": "Offer",
      "price": pkg.discount_percent ? 
        (pkg.base_price * (1 - pkg.discount_percent / 100)).toFixed(2) : 
        pkg.base_price,
      "priceCurrency": pkg.currency || "USD",
      "availability": "https://schema.org/InStock",
      "validFrom": pkg.valid_from,
      "validThrough": pkg.valid_until,
      "seller": {
        "@type": "TravelAgency",
        "name": pkg.partner_name || "Déjà Vu Nomad"
      }
    };

    // Add aggregate rating if reviews exist
    if (pkg.review_count && pkg.avg_rating) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": pkg.avg_rating,
        "reviewCount": pkg.review_count
      };
    }
  }

  // Add duration
  if (pkg.duration_days) {
    schema.duration = `P${pkg.duration_days}D`;
  }

  return schema;
};

// Breadcrumb Schema
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// FAQ Schema - For FAQ pages or sections
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Local Business Schema - For partner pages
export const getTravelAgencySchema = (partner) => {
  if (!partner) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": partner.company_name,
    "url": partner.website,
    "telephone": partner.phone_number,
    "email": partner.email,
    "address": partner.company_address ? {
      "@type": "PostalAddress",
      "streetAddress": partner.company_address
    } : undefined,
    "image": partner.logo,
    "priceRange": "$$"
  };
};

// SoftwareApplication Schema - For app store listings
export const getAppSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Déjà Vu Nomad",
  "operatingSystem": "Web, iOS, Android",
  "applicationCategory": "TravelApplication",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150"
  },
  "description": "Professional travel planning app for group trips. Plan perfect vacations with friends and family using collaborative itineraries, real-time chat, and curated travel packages."
});

// Combined schema for homepage
export const getHomepageSchemas = () => [
  getOrganizationSchema(),
  getWebSiteSchema(),
  getAppSchema()
];

// Generate meta description for packages
export const generatePackageMetaDescription = (pkg) => {
  if (!pkg) return '';
  
  const parts = [];
  
  if (pkg.title) parts.push(pkg.title);
  if (pkg.destination) parts.push(`in ${pkg.destination}`);
  if (pkg.duration_days) parts.push(`${pkg.duration_days} days`);
  if (pkg.base_price) {
    const price = pkg.discount_percent ? 
      (pkg.base_price * (1 - pkg.discount_percent / 100)).toFixed(0) : 
      pkg.base_price;
    parts.push(`from $${price}`);
  }
  if (pkg.partner_name) parts.push(`by ${pkg.partner_name}`);
  
  let description = parts.join(' • ');
  
  if (pkg.highlights?.length > 0) {
    description += `. ${pkg.highlights.slice(0, 2).join(', ')}`;
  }
  
  // Limit to 160 characters for meta description
  if (description.length > 157) {
    description = description.substring(0, 157) + '...';
  }
  
  return description;
};

// Generate keywords for packages
export const generatePackageKeywords = (pkg) => {
  if (!pkg) return '';
  
  const keywords = [
    'travel package',
    'vacation deal',
    pkg.destination,
    pkg.package_type,
    pkg.title,
    `${pkg.destination} tour`,
    `${pkg.destination} vacation`,
    `${pkg.duration_days} day trip`,
    pkg.partner_name,
    'group travel',
    'travel booking'
  ].filter(Boolean);
  
  return [...new Set(keywords)].join(', ');
};

